import { createTheme, alpha } from "@mui/material/styles";
import { tokens } from "./tokens";
import { cssTransition } from "@/components/Chat/motion";

// Graphite surfaces (neutral dark). Kept in sync with the [data-mode="dark"]
// block in styles/theme.css.
const surface = { default: "#121316", paper: "#1c1e22", subtle: "#17191e" };
const ink = {
  primary: "#e7e9ec",
  secondary: "#a6abb4",
  disabled: "rgba(231,233,236,0.40)",
};
// Dark themes are borderless (user decision 2026-07-08): surfaces separate by
// tone, so the divider — which also feeds MUI Paper/AppBar/Drawer borders — is
// fully transparent. Hover/selected fills carry the affordances instead.
const divider = "rgba(255,255,255,0)";

/** Build the dark MUI theme with `primary` = the active accent (a real hex).
 *  AppearanceProvider passes the user's chosen accent (or the dark preset's
 *  default) so MUI controls on the dark themes follow the same accent as the
 *  Tailwind side. Defaults to the brand emerald for the bare `darkTheme`
 *  export. */
export function makeDarkTheme(primary: string = tokens.brand.primary) {
  return createTheme({
  palette: {
    mode: "dark",
    background: { default: surface.default, paper: surface.paper },
    primary: { main: primary, contrastText: "#ffffff" },
    secondary: { main: tokens.brand.secondary, contrastText: tokens.brand.secondaryContrast },
    tertiary: { main: tokens.brand.tertiary, contrastText: tokens.brand.tertiaryContrast },
    text: { primary: ink.primary, secondary: ink.secondary, disabled: ink.disabled },
    divider,
    error: { main: "#e07a72" },
    success: { main: primary },
    warning: { main: tokens.brand.tertiary },
  },
  typography: {
    fontFamily: tokens.fontFamily,
    fontSize: tokens.type.base,
    h5: tokens.type.h5,
    subtitle1: tokens.type.subtitle,
    subtitle2: tokens.type.subtitle,
    body1: { lineHeight: tokens.type.lineHeight },
    body2: { lineHeight: tokens.type.lineHeight },
    button: { textTransform: "none", fontWeight: 600 },
  },
  shape: { borderRadius: tokens.radius.lg },
  components: {
    MuiCssBaseline: {
      styleOverrides: {
        "html, body, #root": { height: "100%" },
        "@media (prefers-reduced-motion: no-preference)": {
          "html, body": { scrollBehavior: "smooth" },
        },
        body: {
          // Track the active preset's canvas so every page (including the
          // not-yet-migrated MUI ones) re-skins when the user picks a theme.
          backgroundColor: `var(--color-canvas, ${surface.default})`,
          overflow: "hidden",
          // Flat surface — no atmosphere gradient in the new design.
          backgroundImage: "none",
          color: ink.primary,
        },
        // Branded keyboard focus ring across every focusable control. Mouse
        // clicks don't trigger :focus-visible, so it only shows on Tab nav.
        ".MuiButtonBase-root:focus-visible, a:focus-visible": {
          outline: "none",
          boxShadow: `0 0 0 2px ${surface.default}, 0 0 0 4px ${primary}`,
        },
        "::selection": { background: tokens.brand.primarySoft, color: "#fff" },
        "*::-webkit-scrollbar": { width: 10, height: 10 },
        "*::-webkit-scrollbar-thumb": { background: "rgba(238,243,240,0.14)", borderRadius: 8, border: "2px solid transparent", backgroundClip: "padding-box" },
        "*::-webkit-scrollbar-thumb:hover": { background: "rgba(238,243,240,0.24)", backgroundClip: "padding-box" },
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          // Preset-aware card surface (falls back to espresso paper). Border
          // stays transparent — dark themes are borderless.
          backgroundColor: `var(--color-surface, ${surface.paper})`,
          backgroundImage: "none",
          border: `1px solid ${divider}`,
          boxShadow: tokens.shadow.md,
        },
      },
    },
    MuiAppBar: {
      styleOverrides: {
        root: { backgroundColor: surface.paper, borderBottom: `1px solid ${divider}`, boxShadow: "none" },
      },
    },
    MuiDrawer: {
      styleOverrides: {
        paper: {
          backgroundColor: surface.subtle,
          borderRight: `1px solid ${divider}`,
          boxShadow: "none",
          backgroundImage: "none",
        },
      },
    },
    MuiButton: {
      styleOverrides: {
        root: {
          // Full pill — the pre-refresh shape (see lightTheme for why).
          borderRadius: tokens.radius.pill,
          transition: cssTransition.lift,
          "&:hover": { transform: "translateY(-1px)" },
          "&:active": { transform: "translateY(0)" },
          "@media (prefers-reduced-motion: reduce)": {
            transition: "box-shadow 200ms ease",
            "&:hover": { transform: "none" },
          },
        },
        // Flat filled buttons — no colored glow/halo on the dark surface.
        contained: {
          boxShadow: "none",
          "&:hover": { boxShadow: "none", filter: "brightness(1.06)" },
        },
        outlined: {
          borderColor: divider,
          "&:hover": { borderColor: "rgba(239,244,241,0.22)", backgroundColor: surface.subtle },
        },
      },
    },
    MuiChip: {
      styleOverrides: {
        root: { backgroundColor: surface.subtle, border: `1px solid ${divider}` },
        outlined: { borderColor: divider },
      },
    },
    // Text boxes: a 1px tonal border, deepening to a 2px accent ring on focus.
    MuiTextField: {
      styleOverrides: {
        root: {
          "& .MuiOutlinedInput-root": {
            backgroundColor: surface.paper,
            borderRadius: tokens.radius.md,
            boxShadow: "none",
            transition: "box-shadow 160ms ease, border-color 160ms ease",
            "& fieldset": { borderColor: "rgba(255,255,255,0.12)" },
            "&:hover fieldset": { borderColor: "rgba(255,255,255,0.18)" },
            "&.Mui-focused": {
              boxShadow: `0 0 0 2px ${alpha(primary, 0.35)}`,
            },
            "&.Mui-focused fieldset": { borderColor: "transparent" },
          },
        },
      },
    },
    MuiTabs: {
      styleOverrides: {
        // Solid accent underline (no gradient).
        indicator: { height: 3, borderRadius: 2, backgroundColor: primary },
      },
    },
    MuiTab: {
      styleOverrides: {
        root: { textTransform: "none", fontWeight: 600, "&.Mui-selected": { color: primary } },
      },
    },
    MuiTooltip: {
      styleOverrides: {
        tooltip: { backgroundColor: "rgba(8,12,10,0.92)", color: "#fff", fontSize: 12 },
      },
    },
    MuiDialog: {
      styleOverrides: {
        paper: {
          backgroundColor: surface.paper,
          backgroundImage: "none",
          border: "1px solid rgba(255,255,255,0.08)",
          borderRadius: tokens.radius.lg,
          boxShadow: "0 18px 44px rgba(0,0,0,0.55)",
        },
      },
    },
    MuiMenu: {
      styleOverrides: {
        paper: {
          backgroundColor: surface.paper,
          backgroundImage: "none",
          border: "1px solid rgba(255,255,255,0.08)",
          borderRadius: tokens.radius.md,
          padding: 6,
          boxShadow: "0 6px 18px rgba(0,0,0,0.5)",
        },
        list: { padding: 0 },
      },
    },
    MuiMenuItem: {
      styleOverrides: {
        root: {
          borderRadius: tokens.radius.control,
          margin: "2px 0",
          transition: "background-color 140ms ease",
          "&:hover": { backgroundColor: surface.subtle },
          "&.Mui-selected": {
            backgroundColor: alpha(primary, 0.16),
            "&:hover": { backgroundColor: alpha(primary, 0.22) },
          },
        },
      },
    },
    MuiIconButton: {
      styleOverrides: {
        root: {
          borderRadius: tokens.radius.control,
          transition: "background-color 160ms ease, color 160ms ease",
          "&:hover": { backgroundColor: surface.subtle },
        },
      },
    },
  },
  });
}

/** Default dark theme (editorial accent) — for tests and any direct import. */
export const darkTheme = makeDarkTheme();
